/**
 * ****************************************************************************
 * @description     :   Global Widget Utilities
 * @created at      :   2025/01/03
 * @created by      :   DaoNX
 * @package         :   Move
 * @copyright       :   Copyright (c) A.N.S Asia
 * @version         :   1.0.0
 * ****************************************************************************
 */

import store from '@/store';

/**
 * Show widget with component
 * @param {Object} config - Widget configuration
 * @param {Component} config.component - Vue component to show
 * @param {Object} config.componentProps - Props to pass to component
 * @param {String} config.position - Widget position ('bottom-right', 'bottom-left', 'top-right', 'top-left', 'bottom-center', 'top-center')
 * @param {Boolean} config.isMobile - Whether it's mobile view
 * @param {String|Object} config.iconElement - Icon element or component
 * @param {Object} config.iconProps - Props for icon element
 * @param {Boolean} config.showCloseButton - Show close button (default: true)
 * @param {Boolean} config.showIconToggle - Always show icon even when content is visible (default: false)
 * @param {Object} config.styles - Style configuration
 * @param {Object} config.classes - Class configuration
 * @param {Object} config.offset - Position offset {x, y}
 * @param {Function} config.onClose - Close callback
 * @param {Function} config.onIconClick - Icon click callback
 */
export const showWidget = (config) => {
    store.commit('app/showWidget', config);
};

/**
 * Hide current widget
 */
export const hideWidget = () => {
    store.commit('app/hideWidget');
};

/**
 * Toggle widget content visibility
 * @param {Boolean} show - Force show/hide (optional)
 */
export const toggleWidgetContent = (show = null) => {
    store.commit('app/toggleWidgetContent', show);
};

/**
 * Update widget configuration
 * @param {Object} config - Configuration to update
 */
export const updateWidgetConfig = (config) => {
    store.commit('app/updateWidgetConfig', config);
};

/**
 * Check if device is mobile
 * @returns {Boolean}
 */
export const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth <= 768;
};

/**
 * Show widget with automatic mobile detection
 * @param {Object} config - Widget configuration (isMobile will be auto-detected if not provided)
 */
export const showWidgetAuto = (config) => {
    const finalConfig = {
        ...config,
        isMobile: config.isMobile !== undefined ? config.isMobile : isMobileDevice()
    };

    showWidget(finalConfig);
};

/**
 * Preset configurations for common widget types
 */
export const widgetPresets = {
    // Chat-like widget
    chat: {
        position: 'bottom-right',
        styles: {
            content: {
                width: '350px',
                height: '500px',
                maxWidth: '90vw',
                maxHeight: '80vh'
            }
        },
        offset: {
            x: 20,
            y: 20
        }
    },

    // Notification widget
    notification: {
        position: 'top-right',
        styles: {
            content: {
                width: '300px',
                maxHeight: '400px'
            }
        },
        offset: {
            x: 20,
            y: 80
        }
    },

    // Help widget
    help: {
        position: 'bottom-right',
        styles: {
            content: {
                width: '320px',
                height: '450px'
            }
        },
        offset: {
            x: 20,
            y: 20
        }
    },

    // Tools widget
    tools: {
        position: 'bottom-left',
        styles: {
            content: {
                width: '250px',
                height: 'auto'
            }
        },
        offset: {
            x: 20,
            y: 20
        }
    }
};

/**
 * Show widget with preset configuration
 * @param {String} presetName - Name of preset ('chat', 'notification', 'help', 'tools')
 * @param {Object} config - Additional configuration to merge with preset
 */
export const showWidgetWithPreset = (presetName, config = {}) => {
    const preset = widgetPresets[presetName];
    if (!preset) {
        console.warn(`Widget preset '${presetName}' not found. Available presets:`, Object.keys(widgetPresets));
        return;
    }

    const finalConfig = {
        ...preset,
        ...config,
        styles: {
            ...preset.styles,
            ...(config.styles || {})
        },
        offset: {
            ...preset.offset,
            ...(config.offset || {})
        }
    };

    showWidgetAuto(finalConfig);
};
